/**
 * Live-drag paint — keep high-frequency motion out of React state.
 *
 * Why this exists: as a retro board fills, dragging used to hitch on the client
 * that owns the pointer. The cause was local, not SignalR (live moves are already
 * fire-and-forget and throttled to ~33ms). Every pointermove called `setStickies`
 * / `setTextLabels`, so CanvasSurface re-rendered and Emotion re-evaluated `sx`
 * on every sticky and label. Cost is O(board size) × pointer rate.
 *
 * Why not a React / MUI upgrade: those still re-render a subtree on setState.
 * Memo / the React Compiler would skip unchanged children but still run the
 * giant parent `.map()` every frame. The fix is to not setState until drop.
 *
 * Contract:
 * - pointermove (local or remote) → write `element.style.left/top` + patch refs
 * - pointerup / itemsCommitted / itemsMoveCommitted → one `setStickies` /
 *   `setTextLabels` + persist (local) or merge (remote)
 * - then clear the inline styles so Emotion `sx` owns position again
 *
 * Remote `itemsMoved` used to `setStickies` every ~33ms, so every observer
 * re-rendered the whole board. Same paint path as local drag; React x/y
 * commits when the gesture is persisted.
 *
 * Do not put `setStickies` / `setTextLabels` back on the pointer-move path
 * (local or remote).
 * Do not use a drag `translate` overlay — stickies already use `transform`
 * for the lift effect (rotate/scale). Mutating left/top matches layout.
 *
 * Locked in RETRO_ROOM_PLAN.md ("Live drag paint"). Same lesson as pan/zoom
 * (reverted forceUpdate) and cursor isolation.
 */

export function applyLiveItemPosition(
  el: HTMLElement | null | undefined,
  x: number,
  y: number,
): void {
  if (!el) return;
  el.style.left = `${x}px`;
  el.style.top = `${y}px`;
}

export function clearLiveItemPosition(el: HTMLElement | null | undefined): void {
  if (!el) return;
  el.style.left = '';
  el.style.top = '';
}

export function applyLiveItemPositions(
  refs: Map<string, HTMLElement>,
  positions: ReadonlyArray<{ id: string; x: number; y: number }>,
): void {
  positions.forEach(({ id, x, y }) => {
    applyLiveItemPosition(refs.get(id), x, y);
  });
}

/**
 * A parent render mid-gesture would otherwise assign `stickiesRef = stickies`
 * and wipe live x/y — both for ids this client is dragging and for ids another
 * client is painting via itemsMoved (React state stays stale until drop).
 * Incoming React state wins for items that are not in `draggedIds`.
 */
export function mergeLiveDragPositions<T extends { id: string; x: number; y: number }>(
  incoming: T[],
  live: T[],
  draggedIds: ReadonlySet<string>,
): T[] {
  if (draggedIds.size === 0) return incoming;
  const liveById = new Map<string, T>();
  for (const item of live) {
    if (draggedIds.has(item.id)) liveById.set(item.id, item);
  }
  if (liveById.size === 0) return incoming;
  return incoming.map(item => {
    const current = liveById.get(item.id);
    if (!current) return item;
    if (current.x === item.x && current.y === item.y) return item;
    return { ...item, x: current.x, y: current.y };
  });
}

export function unionIdSets(a: ReadonlySet<string>, b: ReadonlySet<string>): ReadonlySet<string> {
  if (b.size === 0) return a;
  if (a.size === 0) return b;
  const next = new Set(a);
  b.forEach(id => next.add(id));
  return next;
}

export function clearLiveDragIds(
  ids: Iterable<string>,
  liveIds: Set<string>,
  refs: Map<string, HTMLElement>,
): void {
  Array.from(ids).forEach(id => {
    liveIds.delete(id);
    clearLiveItemPosition(refs.get(id));
  });
}

/**
 * Patch items to absolute x/y, paint the matching DOM nodes, and record the
 * ids as live-dragged so a later React render cannot snap them back.
 */
export function applyRemoteLivePositions<T extends { id: string; x: number; y: number }>(
  items: T[],
  moves: ReadonlyArray<{ id: string; x: number; y: number }>,
  refs: Map<string, HTMLElement>,
  liveIds: Set<string>,
): T[] {
  if (!moves.length) return items;
  const byId = new Map<string, { x: number; y: number }>();
  for (const move of moves) {
    byId.set(move.id, move);
  }
  const painted: { id: string; x: number; y: number }[] = [];
  let changed = false;
  const next = items.map(item => {
    const move = byId.get(item.id);
    if (!move) return item;
    liveIds.add(item.id);
    painted.push({ id: item.id, x: move.x, y: move.y });
    if (item.x === move.x && item.y === move.y) return item;
    changed = true;
    return { ...item, x: move.x, y: move.y };
  });
  applyLiveItemPositions(refs, painted);
  return changed ? next : items;
}

/** Apply one shared (dx, dy) to listed ids, paint DOM, and mark them live-dragged. */
export function applyRemoteLiveDelta<T extends { id: string; x: number; y: number }>(
  items: T[],
  ids: ReadonlyArray<string> | null | undefined,
  dx: number,
  dy: number,
  refs: Map<string, HTMLElement>,
  liveIds: Set<string>,
): T[] {
  if (!ids?.length || (dx === 0 && dy === 0)) return items;
  const idSet = new Set(ids);
  const painted: { id: string; x: number; y: number }[] = [];
  const next = items.map(item => {
    if (!idSet.has(item.id)) return item;
    liveIds.add(item.id);
    const x = item.x + dx;
    const y = item.y + dy;
    painted.push({ id: item.id, x, y });
    return { ...item, x, y };
  });
  applyLiveItemPositions(refs, painted);
  return painted.length ? next : items;
}
